import React, { useState } from 'react';
import { showToast } from '@trellixio/roaster-coffee';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Urls } from 'data';
import { useAPI } from 'hooks';
import Locales from 'locales';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Loader2, Eye, EyeOff, Info } from 'lucide-react';

const ResetPassword = () => {
  const [pwd, setPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [displayPwd, setDisplayPwd] = useState(false);
  const [displayConfirmPwd, setDisplayConfirmPwd] = useState(false);
  const [pwdRuleSize, setPwdRuleSize] = useState(false);
  const [pwdRuleUppercase, setPwdRuleUppercase] = useState(false);
  const [pwdRuleLowercase, setPwdRuleLowercase] = useState(false);
  const [pwdRuleNumber, setPwdRuleNumber] = useState(false);
  const [pwdRuleSpecial, setPwdRuleSpecial] = useState(false);
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const isMutating = queryClient.isMutating() > 0;
  const navigate = useNavigate();

  const { api } = useAPI();

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const { mutate: resetPassword } = useMutation({
    mutationFn: () => api.post('auth/user_token/reset_password/', { token, password: pwd, password2: confirmPwd }),
    onSuccess: () => {
      showToast({
        message: Locales.auth.password_reset_with_success,
        level: 'success',
      });
      navigate(Urls.front.login);
    },
  });

  const isValidPwd = pwdRuleSize && pwdRuleUppercase && pwdRuleLowercase && pwdRuleNumber && pwdRuleSpecial;

  const toggleDisplayPassword = () => setDisplayPwd((val) => !val);
  const toggleDisplayConfirmPassword = () => setDisplayConfirmPwd((val) => !val);

  const validatePassword = (password) => {
    setPwdRuleSize(password.length > 8);
    setPwdRuleUppercase(/[A-Z]/.test(password));
    setPwdRuleLowercase(/[a-z]/.test(password));
    setPwdRuleNumber(/\d/.test(password));
    setPwdRuleSpecial(/[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(password));
  };

  const handlePasswordChange = (value) => {
    const newPassword = value;
    setPwd(newPassword);
    validatePassword(newPassword);
  };

  const enableSubmitButton = isValidPwd && pwd === confirmPwd;

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-hajj-muted via-white to-green-50 flex items-center justify-center p-4 md:pl-[440px]">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-hajj-primary/5 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-20 right-10 w-96 h-96 bg-green-200/20 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '1s' }}
        />
      </div>

      <div className="relative w-full max-w-4xl animate-scale-in">
        <div className="space-y-8">
          <div className="text-center space-y-3">
            <h1 className="text-4xl font-bold text-gray-900 tracking-tight">{Locales.auth.register}</h1>
            <p className="text-base text-gray-600 max-w-sm mx-auto">{Locales.auth.reset_password_message}</p>
          </div>

          {/* <Alert className="border-blue-200 bg-blue-50 text-blue-900 animate-slide-up">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription>
              <p className="font-medium mb-2">{Locales.auth.info_message}</p>
              <ul className="text-sm space-y-1 ml-4 list-disc">
                <li>{Locales.auth.password_rules.size}</li>
                <li>{Locales.auth.password_rules.uppercase}</li>
                <li>{Locales.auth.password_rules.lowercase}</li>
                <li>{Locales.auth.password_rules.number}</li>
                <li>
                  {Locales.auth.password_rules.special} <strong>!@#$%^&*()-=+</strong>
                </li>
                <li>{Locales.auth.password_rules.no_personnal_information}</li>
              </ul>
            </AlertDescription>
          </Alert> */}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start h-full">
            <form className="space-y-6">
              <div className="space-y-3">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  {Locales.auth.email}
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email || ''}
                  disabled
                  className="h-14 text-lg bg-gray-100 border-2 border-gray-200 rounded-xl"
                />
              </div>

              <div className="space-y-3">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  {Locales.auth.password}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={displayPwd ? 'text' : 'password'}
                    placeholder="••••••••••••••••••"
                    value={pwd}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    className="h-14 text-lg bg-white/80 backdrop-blur-sm border-2 border-gray-200 focus:border-hajj-primary focus:ring-4 focus:ring-hajj-primary/10 transition-all duration-200 rounded-xl pr-12"
                  />
                  <button
                    type="button"
                    onClick={toggleDisplayPassword}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {displayPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                  {Locales.auth.confirm_password}
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={displayConfirmPwd ? 'text' : 'password'}
                    placeholder="••••••••••••••••••"
                    value={confirmPwd}
                    onChange={(e) => setConfirmPwd(e.target.value)}
                    className="h-14 text-lg bg-white/80 backdrop-blur-sm border-2 border-gray-200 focus:border-hajj-primary focus:ring-4 focus:ring-hajj-primary/10 transition-all duration-200 rounded-xl pr-12"
                  />
                  <button
                    type="button"
                    onClick={toggleDisplayConfirmPassword}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {displayConfirmPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* <div className="flex flex-wrap gap-2">
                <Badge variant={pwdRuleSize ? 'default' : 'secondary'} className={pwdRuleSize ? 'bg-green-500' : ''}>
                  {Locales.auth.password_rule_size}
                </Badge>
                <Badge
                  variant={pwdRuleUppercase ? 'default' : 'secondary'}
                  className={pwdRuleUppercase ? 'bg-green-500' : ''}
                >
                  {Locales.auth.password_rule_uppercase}
                </Badge>
                <Badge
                  variant={pwdRuleLowercase ? 'default' : 'secondary'}
                  className={pwdRuleLowercase ? 'bg-green-500' : ''}
                >
                  {Locales.auth.password_rule_lowercase}
                </Badge>
                <Badge
                  variant={pwdRuleNumber ? 'default' : 'secondary'}
                  className={pwdRuleNumber ? 'bg-green-500' : ''}
                >
                  {Locales.auth.password_rule_number}
                </Badge>
                <Badge
                  variant={pwdRuleSpecial ? 'default' : 'secondary'}
                  className={pwdRuleSpecial ? 'bg-green-500' : ''}
                >
                  {Locales.auth.password_rule_special}
                </Badge>
              </div> */}

              <Button
                type="button"
                onClick={resetPassword}
                className="w-full h-12 text-base font-medium bg-hajj-primary hover:bg-hajj-secondary text-white rounded-xl shadow-lg shadow-hajj-primary/20 hover:shadow-xl hover:shadow-hajj-primary/30 transition-all duration-200"
                disabled={!enableSubmitButton || isMutating}
              >
                {isMutating ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Création en cours...
                  </>
                ) : (
                  Locales.auth.create_account
                )}
              </Button>
            </form>

            <div className="hidden lg:flex flex-col justify-between h-full space-y-6">
              <Alert className="border-blue-200 bg-blue-50 text-blue-900 animate-slide-up">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription>
                  <p className="font-medium mb-2">{Locales.auth.info_message}</p>
                  <ul className="text-sm space-y-1 ml-4 list-disc">
                    <li>{Locales.auth.password_rules.size}</li>
                    <li>{Locales.auth.password_rules.uppercase}</li>
                    <li>{Locales.auth.password_rules.lowercase}</li>
                    <li>{Locales.auth.password_rules.number}</li>
                    <li>
                      {Locales.auth.password_rules.special} <strong>!@#$%^&*()-=+</strong>
                    </li>
                    <li>{Locales.auth.password_rules.no_personnal_information}</li>
                  </ul>
                </AlertDescription>
              </Alert>

              <div className="flex flex-wrap gap-2 pt-4">
                <Badge variant={pwdRuleSize ? 'default' : 'secondary'} className={pwdRuleSize ? 'bg-green-500' : ''}>
                  {Locales.auth.password_rule_size}
                </Badge>
                <Badge
                  variant={pwdRuleUppercase ? 'default' : 'secondary'}
                  className={pwdRuleUppercase ? 'bg-green-500' : ''}
                >
                  {Locales.auth.password_rule_uppercase}
                </Badge>
                <Badge
                  variant={pwdRuleLowercase ? 'default' : 'secondary'}
                  className={pwdRuleLowercase ? 'bg-green-500' : ''}
                >
                  {Locales.auth.password_rule_lowercase}
                </Badge>
                <Badge
                  variant={pwdRuleNumber ? 'default' : 'secondary'}
                  className={pwdRuleNumber ? 'bg-green-500' : ''}
                >
                  {Locales.auth.password_rule_number}
                </Badge>
                <Badge
                  variant={pwdRuleSpecial ? 'default' : 'secondary'}
                  className={pwdRuleSpecial ? 'bg-green-500' : ''}
                >
                  {Locales.auth.password_rule_special}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
